import { useState, useEffect, useRef } from 'react';
import {
  getSortedData,
  getPaginatedData,
  deletePromotion,
  duplicatePromotion,
} from './promotionapi';

const usePromotionListing = (querySet, triggerResetSort, sortReset, defaultPromotionsData = []) => {
  // State management
  const [promotionsData, setPromotionsData] = useState(defaultPromotionsData);
  const [sortByStartDate, setSortByStartDate] = useState(false);
  const [sortByEndDate, setSortByEndDate] = useState(false);
  const [sortByPublishDate, setSortByPublishDate] = useState(false);
  const [sortByStoreName, setSortByStoreName] = useState(false);
  const [showOffer, setShowOffer] = useState();
  const [activeSort, setActiveSort] = useState('startDate');
  const [page, setPage] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [showLoader, setShowLoader] = useState(false);
  const [query, setQuery] = useState(querySet);
  const [loadingDuplicate, setLoadingDuplicate] = useState(null);
  const [recentlyAddedId, setRecentlyAddedId] = useState(null);
  const paperRef = useRef(null);

  // Handle query changes from props
  useEffect(() => {
    if (querySet?.length) {
      setQuery(querySet);
    }
  }, [querySet]);

  // Update promotions data when default data changes
  useEffect(() => {
    setPromotionsData(defaultPromotionsData);
  }, [defaultPromotionsData]);

  // Handle reset sort trigger
  useEffect(() => {
    if (triggerResetSort) {
      setActiveSort('startDate');
      sortReset();
    }
  }, [triggerResetSort, sortReset]);

  // API interaction functions
  async function getPageData(newPage) {
    setShowLoader(true);
    try {
      const response = await getPaginatedData(newPage, 'START_DATE', 'DESC', query);
      setPromotionsData(response?.data?.data);
    } finally {
      setShowLoader(false);
    }
  }

  async function getData(sortType, order) {
    setShowLoader(true);
    try {
      const response = await getSortedData(sortType, order, query);
      setPromotionsData(response?.data?.data);
    } finally {
      setShowLoader(false);
    }
  }

  async function handleDeletePromotion(id) {
    const shouldDelete = window.confirm('Are you sure you want to delete this promotion?');
    if (!shouldDelete) {
      return;
    }
    try {
      const response = await deletePromotion(id);
      if (response?.data && response?.success === true) {
        setPromotionsData(promotionsData.filter((promotions) => promotions.promotion_id !== id));
        setSnackbarMessage('Promotion deleted successfully');
        setSnackbarSeverity('success');
      }
    } catch (ex) {
      console.error(ex);
      setSnackbarMessage(ex.message);
      setSnackbarSeverity('error');
    } finally {
      setSnackbarOpen(true);
    }
  }

  async function handleDuplicatePromotion(id) {
    setLoadingDuplicate(id);
    try {
      const response = await duplicatePromotion(id);
      if (response?.data && response?.success === true) {
        setPromotionsData([response.data, ...promotionsData]);
        setRecentlyAddedId(response.data.promotion_id);
        setSnackbarMessage('Duplicated the promotion successfully');
        setSnackbarSeverity('success');

        // Remove highlight after a few seconds
        setTimeout(() => {
          setRecentlyAddedId(null);
        }, 3000);
      }
    } catch (ex) {
      console.error(ex);
      setSnackbarMessage('Error while duplicating promotion, please try again');
      setSnackbarSeverity('error');
    } finally {
      setLoadingDuplicate(null);
      setSnackbarOpen(true);
    }
  }

  // Sort handler functions
  const handleChangePage = (event, newPage) => {
    getPageData(newPage);
    setPage(newPage);
  };

  const handleStartDateSort = (order) => {
    if (order) {
      getData('START_DATE', 'DESC');
      setActiveSort('startDate');
    } else {
      getData('START_DATE', 'ASC');
      setActiveSort('');
    }
    setSortByStartDate(!sortByStartDate);
  };

  const handleEndDateSort = (order) => {
    if (order) {
      getData('END_DATE', 'DESC');
      setActiveSort('endDate');
    } else {
      getData('END_DATE', 'ASC');
      setActiveSort('');
    }
    setSortByEndDate(!sortByEndDate);
  };

  const handlePublishDateSort = (order) => {
    if (order) {
      getData('PUBLISH_DATE', 'DESC');
      setActiveSort('publishDate');
    } else {
      getData('PUBLISH_DATE', 'ASC');
      setActiveSort('');
    }
    setSortByPublishDate(!sortByPublishDate);
  };

  const handleStoreSort = (order) => {
    if (order) {
      getData('STORE_NAME', 'DESC');
      setActiveSort('storeName');
    } else {
      getData('STORE_NAME', 'ASC');
      setActiveSort('');
    }
    setSortByStoreName(!sortByStoreName);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Generate headline functions
  const generateHistoricHeadline = (data) => {
    if (data?.flip) {
      const sweetenerText = data?.headline?.SWEETENER?.length
        ? `PLUS ${data?.headline?.SWEETENER}`
        : '';
      return `${data?.headline?.FINANCE} ${data?.conjunction} ${data?.headline?.DISCOUNT} ${sweetenerText}`;
    } else {
      const sweetenerText = data?.headline?.SWEETENER?.length
        ? `PLUS ${data?.headline?.SWEETENER}`
        : '';
      return `${data?.headline?.DISCOUNT} ${data?.conjunction} ${data?.headline?.FINANCE} ${sweetenerText}`;
    }
  };

  // Exposing needed values and functions
  return {
    // Data states
    promotionsData,
    sortByStartDate,
    sortByEndDate,
    sortByPublishDate,
    sortByStoreName,
    showOffer,
    activeSort,
    page,
    snackbarOpen,
    snackbarMessage,
    snackbarSeverity,
    showLoader,
    loadingDuplicate,
    recentlyAddedId,
    paperRef,

    // Action handlers
    setShowOffer,
    handleSnackbarClose,
    handleChangePage,
    handleStartDateSort,
    handleEndDateSort,
    handlePublishDateSort,
    handleStoreSort,
    handleDeletePromotion,
    handleDuplicatePromotion,

    // Helper functions
    generateHistoricHeadline,
  };
};

export default usePromotionListing;
